import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

const LoungeQuestion = ({ question, setQuestionAnswer }) => {
  const { i18n } = useTranslation();
  const [selectedRating, setSelectedRating] = useState(null);
  const [hoveredRating, setHoveredRating] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [textValue, setTextValue] = useState("");
  const [openText, setOpenText] = useState("");

  const {
    id,
    order,
    textArabic,
    textEnglish,
    type,
    options,
    answer,
    openAnswer,
    openAnswerTitleArabic,
    openAnswerTitleEnglish,
  } = question;

  // Helper function to remove "Please rate:" prefix from question text
  const cleanQuestionText = (text, isArabic) => {
    if (!text) return text;
    if (isArabic) {
      // Remove "يرجى تقييم" from Arabic text
      return text.replace(/^يرجى تقييم\s*/i, "").trim();
    } else {
      // Remove "Please rate:" from English text
      return text.replace(/^Please rate:\s*/i, "").trim();
    }
  };

  const displayTextEnglish = cleanQuestionText(textEnglish, false);
  const displayTextArabic = cleanQuestionText(textArabic, true);

  // Initialize state from answer
  useEffect(() => {
    if (answer) {
      if (type === "multipleChoice") {
        const isStarRating = 
          options &&
          options.length === 5 &&
          options.every((opt, idx) => opt.value === (idx + 1).toString());
        
        if (isStarRating) {
          const values = answer.split(',').filter(a => a).map(v => parseInt(v));
          setSelectedRating(values.length > 0 ? Math.max(...values) : null);
        } else {
          setSelectedOptions(answer.split(',').filter(a => a));
        }
      } else if (type === "oneChoice" || type === "numericOneChoice") {
        setSelectedOption(answer);
      } else {
        // Check if it's a text question - Type 2 = Text
        const typeNum = typeof type === 'number' ? type : parseInt(type);
        if (type === "text" || type === "Text" || typeNum === 2 || type === "2") {
          setTextValue(answer);
        } else if (type === "oneChoiceWithOpenAnswer") {
          setSelectedOption(answer);
          setOpenText(openAnswer || "");
        }
      }
    } else {
      setSelectedRating(null);
      setSelectedOption(null);
      setSelectedOptions([]);
      setTextValue("");
      setOpenText("");
    }
  }, [id, answer, openAnswer, type, options]);

  // Star Rating Handler
  const handleStarClick = (starValue) => {
    setSelectedRating(starValue);
    const option = options?.find(opt => opt.value === starValue.toString());
    setQuestionAnswer(starValue.toString(), option?.nextQuestionId || null);
  };

  const handleStarHover = (rating) => {
    setHoveredRating(rating);
  };

  const handleStarLeave = () => {
    setHoveredRating(null);
  };

  const getStarDisplay = (starIndex) => {
    const rating = hoveredRating || selectedRating;
    if (rating && starIndex <= rating) {
      return "★";
    }
    return "☆";
  };

  const getStarStyle = (starIndex) => {
    const rating = hoveredRating || selectedRating;
    const isActive = rating && starIndex <= rating;
    return {
      fontSize: "32px",
      color: isActive ? "#FFD700" : "#ccc",
      cursor: "pointer",
      margin: "0 5px",
      transition: "color 0.2s ease",
      userSelect: "none",
    };
  };

  // Check if question is star rating (5 options with values 1-5)
  const isStarRating = 
    (type === "oneChoice" || type === "multipleChoice") &&
    options &&
    options.length === 5 &&
    options.every((opt, idx) => opt.value === (idx + 1).toString());

  // Render Star Rating
  if (isStarRating || type === "starRating") {
    return (
      <section className="section section-lg" style={{ padding: "5px 0", marginBottom: "0" }}>
        <div className="container rborder oneChoice" style={{ marginBottom: "0" }}>
          <div>
            <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "15px",
                    padding: "5px 0",
                    direction: i18n.language === "ar" ? "rtl" : "ltr",
                  }}
                >
                  <h3 style={{ margin: 0, flex: "1 1 auto" }}>
                    {`${order}- `}
                    {i18n.language === "en" ? displayTextEnglish : displayTextArabic}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexShrink: 0,
                      flexDirection: i18n.language === "ar" ? "row-reverse" : "row",
                    }}
                  >
                  {[1, 2, 3, 4, 5].map((starIndex) => {
                    const option = options?.find(opt => opt.value === starIndex.toString());
                    return (
                      <span
                        key={starIndex}
                        onClick={() => handleStarClick(starIndex)}
                        onMouseEnter={() => handleStarHover(starIndex)}
                        onMouseLeave={handleStarLeave}
                        style={getStarStyle(starIndex)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleStarClick(starIndex);
                          }
                        }}
                        aria-label={`Rate ${starIndex} out of 5`}
                      >
                        {getStarDisplay(starIndex)}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Render Numeric One Choice
  if (type === "numericOneChoice") {
    const getOptionClass = (value) => {
      let baseClass = "";
      switch (value) {
        case "1": baseClass = "one"; break;
        case "2": baseClass = "two"; break;
        case "3": baseClass = "three"; break;
        case "4": baseClass = "four"; break;
        case "5": baseClass = "five"; break;
        case "6": baseClass = "six"; break;
        case "7": baseClass = "seven"; break;
        case "8": baseClass = "eight"; break;
        case "9": baseClass = "nine"; break;
        case "10": baseClass = "ten"; break;
        default: break;
      }
      return selectedOption === value ? `${baseClass} selected` : baseClass;
    };

    return (
      <section className="section section-lg" style={{ padding: "5px 0" }}>
        <div className="container rborder">
          <h3>{i18n.language === "en" ? displayTextEnglish : displayTextArabic}</h3>
          <div className="number-btn-section">
            <div className="btn-label-text">
              {question.leftTextEnglish && (
                <span className={i18n.language === "en" ? "left" : "right"}>
                  {i18n.language === "en" ? question.leftTextEnglish : question.leftTextArabic}
                </span>
              )}
              {question.rightTextEnglish && (
                <span className={i18n.language === "en" ? "right" : "left"}>
                  {i18n.language === "en" ? question.rightTextEnglish : question.rightTextArabic}
                </span>
              )}
            </div>
            <div>
              {options?.map((option) => (
                <button
                  key={option.value}
                  className={getOptionClass(option.value)}
                  type="button"
                  onClick={() => {
                    setSelectedOption(option.value);
                    setQuestionAnswer(option.value, option.nextQuestionId);
                  }}
                >
                  {i18n.language === "en" ? option.textEnglish : option.textArabic}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Render Multiple Choice
  if (type === "multipleChoice") {
    const handleCheckboxChange = (optionValue, isChecked) => {
      let newSelectedOptions;
      if (isChecked) {
        newSelectedOptions = [...selectedOptions, optionValue];
      } else {
        newSelectedOptions = selectedOptions.filter(opt => opt !== optionValue);
      }
      setSelectedOptions(newSelectedOptions);
      const option = options.find(opt => opt.value === optionValue);
      setQuestionAnswer(newSelectedOptions.join(','), option?.nextQuestionId, isChecked);
    };

    return (
      <section className="section section-lg" style={{ padding: "5px 0", marginBottom: "0" }}>
        <div className="container rborder">
          <div className="table-responsive">
            <table className="yes-no-table">
              <tr>
                <th colSpan="2">
                  <h3>{i18n.language === "en" ? displayTextEnglish : displayTextArabic}</h3>
                  <span style={{ color: '#00bfb3' }}>{i18n.t('you_can_choose_multiple_options')}</span>
                </th>
              </tr>
              {options?.map(option => (
                <tr key={option.value}>
                  <th nowrap="nowrap">
                    <label>{i18n.language === "en" ? option.textEnglish : option.textArabic}</label>
                  </th>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedOptions.includes(option.value)}
                      onChange={(event) =>
                        handleCheckboxChange(option.value, event.target.checked)}
                    />
                  </td>
                </tr>
              ))}
            </table>
          </div>
        </div>
      </section>
    );
  }

  // Render Text Question (type === "text" or type === 2 or type === "Text")
  // Check if it's a text question - handle various type formats
  // Type 2 = Text (enum: NumericOneChoice=0, MultipleChoice=1, Text=2, ...)
  const typeNum = typeof type === 'number' ? type : parseInt(type);
  const isTextQuestion = 
    type === "text" || 
    type === "Text" || 
    typeNum === 2 ||
    type === "2";
  
  if (isTextQuestion) {
    const handleTextChange = (event) => {
      const value = event.target.value;
      setTextValue(value);
      const nextQuestionId = options && options.length > 0 ? options[0].nextQuestionId : null;
      // Always call setQuestionAnswer to save the answer, even if empty
      setQuestionAnswer(value, nextQuestionId);
      console.log(`Text question ${order} answer updated:`, value);
    };

    return (
      <section className="section section-lg" style={{ padding: "5px 0" }}>
        <div className="container rborder lounge">
          <h3>
            {order ? `${order}- ` : ""}
            {i18n.language === "en" ? displayTextEnglish : displayTextArabic}
          </h3>
          <div className="number-btn-section" style={{ marginTop: "15px" }}>
            <textarea
              style={{ width: "100%", padding: "10px", fontSize: "16px", border: "1px solid #ccc", borderRadius: "4px" }}
              rows="5"
              value={textValue}
              onChange={handleTextChange}
              placeholder={i18n.language === "en" ? "Enter your comments or suggestions..." : "أدخل ملاحظاتك أو اقتراحاتك..."}
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
            />
          </div>
        </div>
      </section>
    );
  }

  // Render One Choice With Open Answer
  if (type === "oneChoiceWithOpenAnswer") {
    const handleRadioChange = (option) => {
      setSelectedOption(option.value);
      setQuestionAnswer(option.value, option.nextQuestionId, false, option.withText ? openText : '');
    };

    const handleTextAreaChange = (event) => {
      setOpenText(event.target.value);
      const selectedOpt = options.find(opt => opt.value === selectedOption);
      setQuestionAnswer(selectedOption, selectedOpt?.nextQuestionId, false, event.target.value);
    };

    return (
      <section className="section section-lg" style={{ padding: "5px 0" }}>
        <div className="container rborder oneChoice">
          <div>
            <div>
              <h3>{`${order}- `}{i18n.language === 'en' ? displayTextEnglish : displayTextArabic}</h3>
              <div className="row">
                {options?.map((option) => (
                  <div key={option.value} className="col-md-4 oneChoiceOption" style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="radio"
                      value={option.value}
                      checked={selectedOption === option.value}
                      onChange={() => handleRadioChange(option)}
                      style={{ flexShrink: 0 }}
                    />
                    <label style={{
                      [i18n.language === "ar" ? "marginRight" : "marginLeft"]: "8px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}>{i18n.language === 'en' ? option.textEnglish : option.textArabic}</label>
                  </div>
                ))}
              </div>
            </div>
            {selectedOption && options?.find((option) => option.value === selectedOption)?.withText && (
              <div className="open-answer">
                <h3>{i18n.language === 'en' ? openAnswerTitleEnglish : openAnswerTitleArabic}</h3>
                <div className="number-btn-section">
                  <textarea
                    rows="3"
                    value={openText}
                    onChange={handleTextAreaChange}
                    dir={i18n.language === "ar" ? "rtl" : "ltr"}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Render One Choice (default)
  return (
    <section className="section section-lg" style={{ padding: "10px 0" }}>
      <div className="container rborder oneChoice">
        <div>
          <div>
            <h3>
              {`${order}- `}
              {i18n.language === "en" ? displayTextEnglish : displayTextArabic}
            </h3>
            <div className="row">
              {options?.map((option, index) => (
                <div
                  key={index}
                  className="col-md-6 oneChoiceOption"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <input
                    id={`radio-${id}-${index}`}
                    name={`radio-${id}`}
                    type="radio"
                    value={option.value}
                    checked={selectedOption === option.value}
                    onChange={() => {
                      setSelectedOption(option.value);
                      setQuestionAnswer(option.value, option.nextQuestionId);
                    }}
                    style={{ flexShrink: 0 }}
                  />
                  <label
                    htmlFor={`radio-${id}-${index}`}
                    style={{
                      [i18n.language === "ar" ? "marginRight" : "marginLeft"]: "8px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {i18n.language === "en"
                      ? option.textEnglish
                      : option.textArabic}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoungeQuestion;

